import { FC, useEffect } from 'react';
import { RiskProfile } from '@/api';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/redux/hooks';
import { Switch, Tooltip } from 'antd';
import { riskscoreProfilesSelectors } from '@/redux/selectors';
import { riskscoreProfilesActions } from '@/redux/actions';

import { DeleteOutlined } from '@ant-design/icons';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { SvgIcon } from '@/components/SvgIcon';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './RiskscoreProfileModal.module.sass';

interface RiskscoreProfileModalProps extends ModalInfoProps {
  onCreateProfile: () => void;
  onActivate: (profileId: number) => void;
  onDetails: (profileId: number) => void;
  onDelete: (profile: RiskProfile) => void;
}

const RiskscoreProfileModal: FC<RiskscoreProfileModalProps> = (props) => {
  const { isOpen, onClose, onCreateProfile, onActivate, onDetails, onDelete } =
    props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: riskProfileData, loading } = useAppSelector(
    riskscoreProfilesSelectors.getRiskscoreProfilesSlice,
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(riskscoreProfilesActions.listRiskProfiles({}));
    }
  }, [isOpen]);

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.riskscore_modal_title')}
      className={styles.modal}
      actions={
        <div className={styles.footerActions}>
          <Button
            type="primary"
            size="m"
            corner="rect"
            onClick={onCreateProfile}
            disabled={false}
          >
            <SvgIcon name="plus" />
            <span>{t('translation.create_risk_profile_btn')}</span>
          </Button>

          <Button
            size="m"
            type="secondary"
            corner="rect"
            onClick={onClose}
            disabled={false}
          >
            {t('translation.cancel_btn')}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <div className={styles.form}>
        <div className={styles.list}>
          {loading && (
            <div className={styles.loader}>
              <Loader size="small" />
            </div>
          )}
          {riskProfileData?.profiles?.map((profile: RiskProfile) => {
            return (
              <div className={styles.item} key={profile.id}>
                <Tooltip
                  zIndex={3000}
                  title={
                    profile.is_enabled
                      ? t('translation.profile_disable_warn')
                      : ''
                  }
                >
                  <Switch
                    checked={profile.is_enabled}
                    disabled={profile.is_enabled}
                    onChange={() => onActivate(profile.id)}
                  />
                </Tooltip>

                <div className={styles.name}>{profile.name}</div>

                <div className={styles.actions}>
                  {!profile.is_enabled && !profile.locked && (
                    <div className={styles.hiddenActions}>
                      <button
                        type="button"
                        title={t('translation.delete_btn') || ''}
                        className={styles.delete}
                        onClick={() => onDelete(profile)}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  )}

                  <Button
                    size="s"
                    type="bordered"
                    corner="rect"
                    onClick={() => onDetails(profile.id)}
                    disabled={false}
                  >
                    {t('translation.details')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalInfo>
  );
};

export default RiskscoreProfileModal;
